import {
  Box,
  Flex,
  HStack,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverHeader,
  useBreakpointValue,
  useColorMode,
} from "@chakra-ui/react";
import ProfileAvatar from "components/profile/profile-avatar";
import {
  BodySize,
  BodyText,
  Color,
  FWeight,
  Heading,
  HeadingSize,
  Icon,
  IconType,
} from "theme/old-design-system/styled-components";
import CalendarBookingDomain from "entities/domain/calendar/calendar-domain";
import useAgentsStore from "hooks/use-agents-store";
import useCalendarStore from "hooks/use-calendar-store";
import useContactsStore from "hooks/use-contacts-store";
import useCustomChakraTheme from "hooks/use-custom-chakra-theme";
import React from "react";
import { toast } from "react-toastify";

interface CalendarEventInfoProps {
  currentEvent: CalendarBookingDomain;
  closeEventPopover: () => void;
  onEditClick: () => void;
  assignedAgentId: number | null;
}

const EventInfoPopover = ({
  currentEvent,
  closeEventPopover,
  onEditClick,
  assignedAgentId,
}: CalendarEventInfoProps) => {
  const { colorMode } = useColorMode();
  const { colorScheme } = useCustomChakraTheme();
  const { getAgentById } = useAgentsStore();
  const { filterContactById } = useContactsStore();
  const { deleteEvent, errors } = useCalendarStore();

  const customer = filterContactById(currentEvent.customerId);

  const EventInfoTopBar = (
    <Flex alignItems="center" justifyContent="space-between" w="100%">
      <Flex alignItems="center" justifyContent="start">
        <Icon
          width={20}
          height={20}
          mr={4}
          type={IconType.TRASH}
          pathFill={Color.SMOKY_GREY}
          onClick={() => {
            deleteEvent({ bookingId: currentEvent.id });
            closeEventPopover();
            if (errors.length) {
              toast.error(errors[0]);
            } else {
              toast.success("Event deleted successfully!");
            }
          }}
        />
        <Icon
          type={IconType.EDIT}
          pathFill={Color.SMOKY_GREY}
          onClick={onEditClick}
          width={20}
          height={20}
        />
      </Flex>
      <Icon
        type={IconType.CLOSE}
        onClick={() => closeEventPopover()}
        pathFill={Color.SMOKY_GREY}
        width={20}
        height={20}
      />
    </Flex>
  );

  const EventContent = (
    <>
      <Box padding="8px 12px">
        <Box
          borderLeft={`"3px solid ${
            assignedAgentId
              ? getAgentById(assignedAgentId).color.value
              : `${colorScheme}.400`
          }`}
          pl="1rem"
          mb="1rem"
        >
          <Heading size={HeadingSize.TWO}> {currentEvent?.title} </Heading>

          <BodyText size={BodySize.THREE} fweight={FWeight.THREE}>
            {currentEvent.getFormatDate()}
          </BodyText>
        </Box>
      </Box>

      {currentEvent.description && (
        <Box ml="15px" mb="10px">
          <BodyText size={BodySize.THREE} fweight={FWeight.THREE}>
            {currentEvent.description}
          </BodyText>
        </Box>
      )}

      {customer && (
        <Box ml="15px" mb="10px">
          <BodyText size={BodySize.THREE} fweight={FWeight.ONE}>
            Customer
          </BodyText>
          <Flex mt="15px" alignItems="center">
            <Box
              display="inline-block"
              width="10%"
              verticalAlign="middle"
              mr="8px"
            >
              <ProfileAvatar
                profilePicture={customer.getPicture()}
                name={customer.getDisplayName()}
              />
            </Box>
            <div>
              <BodyText size={BodySize.THREE} fweight={FWeight.THREE}>
                {customer.getDisplayName()}
              </BodyText>
              <BodyText size={BodySize.TWO} fweight={FWeight.THREE}>
                {customer.getPhoneNum()
                  ? customer.getPhoneNum()
                  : customer.getEmailAddress()}
              </BodyText>
            </div>
          </Flex>
        </Box>
      )}

      {currentEvent.agentIds.length !== 0 && (
        <Box ml="15px" mb="10px">
          <BodyText size={BodySize.THREE} fweight={FWeight.ONE}>
            {currentEvent.agentIds.length === 1 ? "Agent" : "Agents"}
          </BodyText>

          {currentEvent.agentIds.map((agent) => (
            <div key={agent}>
              <Flex mt="15px" alignItems="center">
                <Box
                  background={getAgentById(agent).color.value}
                  borderRadius="50%"
                  width="12px"
                  height="12px"
                  mr="10px"
                  ml="10px"
                />
                <BodyText size={BodySize.THREE} fweight={FWeight.THREE}>
                  {" "}
                  {getAgentById(agent).getFullName()}{" "}
                </BodyText>
              </Flex>
            </div>
          ))}
        </Box>
      )}
    </>
  );

  const MobileInfo = (
    <>
      <Flex
        flexFlow="column nowrap"
        width="100%"
        bgColor={colorMode === "dark" ? "gray.800" : "gray.50"}
        borderRadius="lg"
      >
        <Flex
          flexFlow="column nowrap"
          border="none"
          width="100%"
          maxWidth="100%!important"
          marginTop="0!important"
          height="100%"
          padding="0 16px"
          borderRightWidth="1px"
          borderRightStyle="solid"
          borderRightColor="gray.50"
        >
          <HStack
            w="100%"
            justify="flex-end"
            align="center"
            pt="16px"
            mb="24px"
          >
            {EventInfoTopBar}
          </HStack>

          {EventContent}
        </Flex>
      </Flex>
    </>
  );

  const DesktopInfo = (
    <>
      <Popover
        closeOnBlur={true}
        closeOnEsc={true}
        isOpen={!!currentEvent}
        onClose={closeEventPopover}
      >
        <PopoverContent>
          <PopoverArrow />

          <PopoverHeader display="flex" justifyContent="flex-end" border="none">
            {EventInfoTopBar}
          </PopoverHeader>

          {EventContent}
        </PopoverContent>
      </Popover>
    </>
  );

  const isBaseSize = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );

  return isBaseSize ? MobileInfo : DesktopInfo;
};

export default EventInfoPopover;
